import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-emerald-700 text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/">
            <Button
              variant="ghost"
              className="text-white hover:bg-emerald-600 mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-10 w-10" />
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-emerald-100 mt-2">How we collect, use and protect your personal data</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none space-y-6">
            <p className="text-gray-700">
              This Privacy Policy explains how My Selfie Moments Co Ltd ("we", "us", "our") collects, uses, and protects your personal data 
              in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>

            <hr className="my-8" />

            {/* Section 1 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Who We Are</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-gray-700">
                <p><strong>Business name:</strong> My Selfie Moments Co Ltd</p>
                <p><strong>Contact email:</strong> <a href="mailto:info@myselfiemoments.co.uk" className="text-emerald-700 hover:text-emerald-800 underline">info@myselfiemoments.co.uk</a></p>
                <p><strong>Data Controller:</strong> My Selfie Moments Co Ltd</p>
              </div>
              <p className="text-gray-700 mt-4">We are the data controller responsible for your personal data.</p>
            </div>

            <hr className="my-8" />

            {/* Section 2 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. What Personal Data We Collect</h2>
              <p className="text-gray-700 mb-4">We may collect and process the following types of personal data:</p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.1 Booking & Enquiry Data</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Telephone number</li>
                    <li>Billing address</li>
                    <li>Event details (date, time, venue, company name where applicable)</li>
                    <li>Invoice and payment information (excluding full card details)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.2 Event Image Data</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Photographs and/or videos taken during Photo Booth use</li>
                    <li>Image metadata (date/time, booth ID)</li>
                    <li>Optional guest-provided data for sharing features (e.g. email address or phone number)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.3 Website Data</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>IP address</li>
                    <li>Browser and device information</li>
                    <li>Cookies and usage data</li>
                  </ul>
                </div>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 3 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Personal Data</h2>
              <p className="text-gray-700 mb-3">We use personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>To respond to enquiries and manage bookings</li>
                <li>To deliver Photo Booth services at events</li>
                <li>To issue invoices and process payments</li>
                <li>To operate sharing features (e.g. email delivery, online galleries)</li>
                <li>To comply with legal and accounting obligations</li>
                <li>To improve our services and website</li>
                <li>For marketing and promotional purposes (where consent has been obtained or is lawful)</li>
              </ul>
            </div>

            <hr className="my-8" />

            {/* Section 4 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Bases for Processing</h2>
              <p className="text-gray-700 mb-3">Under UK GDPR, we rely on the following lawful bases:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Contract</strong> – where processing is necessary to perform a booking or agreement</li>
                <li><strong>Legitimate interests</strong> – to operate and improve our business</li>
                <li><strong>Consent</strong> – for marketing communications or optional image use</li>
                <li><strong>Legal obligation</strong> – for tax, accounting or regulatory compliance</li>
              </ul>
            </div>

            <hr className="my-8" />

            {/* Section 5 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Image Capture & Event Photography</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>5.1</strong> Our Photo Booth services involve the capture of photographs and/or videos at events.</p>
                <p><strong>5.2</strong> For private and corporate events, the event organiser (Client) is responsible for informing attendees that images will be captured and for obtaining any necessary permissions or consents.</p>
                <p><strong>5.3</strong> Where required, signage may be displayed at events to notify guests of image capture.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 6 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Software & International Data Transfers</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>6.1</strong> We use third-party Photo Booth software provided by Photo Booth Supply Co. (Fiesta App) to operate our Photo Booth services.</p>
                <p><strong>6.2</strong> Fiesta is based in the United States, and images, videos and related data may be processed or temporarily stored on servers outside the UK.</p>
                <p><strong>6.3</strong> Such transfers are carried out for the purpose of delivering contracted services, including instant sharing, galleries and cloud-based functionality.</p>
                <p><strong>6.4</strong> We do not control the data handling practices, security measures or service availability of third-party software providers and recommend reviewing their privacy policies where applicable.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 7 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Sharing Personal Data</h2>
              <p className="text-gray-700 mb-3">We may share personal data with:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Payment processors and accounting providers</li>
                <li>IT, website and hosting providers</li>
                <li>Photo Booth software providers (including Fiesta)</li>
                <li>Professional advisers (e.g. accountants, insurers)</li>
                <li>Legal or regulatory authorities where required by law</li>
              </ul>
              <p className="text-gray-700 mt-4 font-semibold">We do not sell personal data.</p>
            </div>

            <hr className="my-8" />

            {/* Section 8 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>8.1</strong> We retain personal data only for as long as necessary to fulfil the purposes outlined in this policy.</p>
                <p><strong>8.2</strong> Booking and invoice records are retained for legal and accounting purposes.</p>
                <p><strong>8.3</strong> Event images are retained for a reasonable period for service delivery, backup and administration, unless deletion is requested in writing.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 9 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Marketing Communications</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>9.1</strong> We may use images from events for marketing purposes where permitted under our Terms & Conditions or where consent has been obtained.</p>
                <p><strong>9.2</strong> You may opt out of marketing communications or image use at any time by contacting us in writing at <a href="mailto:info@myselfiemoments.co.uk" className="text-emerald-700 hover:text-emerald-800 underline">info@myselfiemoments.co.uk</a>.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 10 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Your Data Protection Rights</h2>
              <p className="text-gray-700 mb-3">Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request erasure of data (where applicable)</li>
                <li>Restrict or object to processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-gray-700 mt-4">Requests can be made by contacting us using the details above.</p>
            </div>

            <hr className="my-8" />

            {/* Section 11 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookies</h2>
              <div className="space-y-3 text-gray-700">
                <p>Our website may use cookies to improve functionality and user experience.</p>
                <p>You can control or disable cookies through your browser settings.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 12 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Data Security</h2>
              <div className="space-y-3 text-gray-700">
                <p>We take reasonable technical and organisational measures to protect personal data against unauthorised access, loss or misuse.</p>
                <p>However, no system is completely secure, and we cannot guarantee absolute security of data transmitted electronically.</p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 13 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Complaints</h2>
              <div className="space-y-3 text-gray-700">
                <p>If you have concerns about how we handle your data, please contact us in the first instance.</p>
                <p>You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
                <p><a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-800 underline font-medium">www.ico.org.uk</a></p>
              </div>
            </div>

            <hr className="my-8" />

            {/* Section 14 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to This Policy</h2>
              <div className="space-y-3 text-gray-700">
                <p>We may update this Privacy Policy from time to time. The most recent version will always be available on our website.</p>
              </div>
            </div>

            <hr className="my-8" />

            <div className="bg-emerald-50 p-6 rounded-lg mt-8 border-l-4 border-emerald-700">
              <h3 className="font-semibold text-gray-900 mb-2">Contact Us</h3>
              <p className="text-sm text-gray-700">
                If you have any questions about this Privacy Policy or how we handle your data, please contact us:
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>Email:</strong> <a href="mailto:info@myselfiemoments.co.uk" className="text-emerald-700 hover:text-emerald-800 underline">info@myselfiemoments.co.uk</a>
              </p>
              <p className="text-sm text-gray-600 mt-4">
                <strong>Last Updated:</strong> November 2025
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/">
            <Button
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Website
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
